import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ShoppingCart, Clock } from 'lucide-react';
import { ordersService } from '@services/orders.service';
import { formatCurrency } from '@lib/utils';
import { ForceEndTripModal } from '@components/delivery/ForceEndTripModal';

// §11c: the green intensity ramp. An order is a journey, not a state. Every
// in-progress step used to be the SAME mint chip, so an operator scanning the
// queue could not tell "accepted" from "en route" without reading the label.
// The hue is held and the intensity climbs, so the ramp is legible at a glance
// and still readable for a colour-blind operator (it is a lightness ramp).
//
// `pending` stays WARNING on purpose: it is the one step that genuinely needs
// attention, because it is waiting on the merchant. Everything from `accepted`
// onward is healthy forward motion and must not wear an alarm colour.
//
// The ramp terminates into --state-success; it does not replace it.
const statusChip: Record<string, string> = {
  pending:         'bg-[var(--state-warning-bg)] text-[var(--state-warning)]',
  accepted:        'bg-[var(--progress-1)] text-[var(--progress-1-fg)]',
  preparing:       'bg-[var(--progress-2)] text-[var(--progress-2-fg)]',
  processing:      'bg-[var(--progress-2)] text-[var(--progress-2-fg)]',
  ready_for_pickup:'bg-[var(--progress-3)] text-[var(--progress-3-fg)]',
  assigned:        'bg-[var(--progress-3)] text-[var(--progress-3-fg)]',
  en_route:        'bg-[var(--progress-4)] text-[var(--progress-4-fg)]',
  delivered:       'bg-[var(--state-success-bg)] text-[var(--state-success)]',
  confirmed:       'bg-[var(--state-success-bg)] text-[var(--state-success)]',
  completed:       'bg-[var(--state-success-bg)] text-[var(--state-success)]',
  cancelled:       'bg-[var(--state-danger-bg)] text-[var(--state-danger)]',
  declined:        'bg-[var(--state-danger-bg)] text-[var(--state-danger)]',
  rejected:        'bg-[var(--state-danger-bg)] text-[var(--state-danger)]',
  failed:          'bg-[var(--state-danger-bg)] text-[var(--state-danger)]',
  refunded:        'bg-[var(--bg-base)] text-[var(--text-muted)]',
};

const DELIVERY_ENDED_STATUSES = ['delivered', 'completed', 'cancelled', 'refunded', 'returned'];

export const OrderDetailPage = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const queryClient = useQueryClient();
  const [showForceEnd, setShowForceEnd] = useState(false);

  const { data: order, isLoading } = useQuery({
    queryKey: ['order', orderId],
    queryFn: () => ordersService.get(orderId!),
    enabled: !!orderId,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[var(--bg-base)] p-8 text-center text-[14px] text-[var(--text-muted)]">
        Loading order...
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-[var(--bg-base)] p-8 text-center text-[14px] text-[var(--state-danger)]">
        Order not found.
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg-base)] p-8 space-y-8">
      {/* Back navigation */}
      <Link
        to="/orders"
        className="inline-flex items-center gap-2 text-[13px] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
      >
        <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
        Back to Orders
      </Link>

      {/* Page header */}
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)]">
            Order detail
          </p>
          <div className="flex items-center gap-3">
            <ShoppingCart className="w-7 h-7 text-[var(--text-accent)]" strokeWidth={1.5} />
            <h1 className="font-display text-[32px] font-medium tracking-[-0.02em] text-[var(--text-primary)]">
              #{order.branchOrderNumber || order.id.slice(0, 8)}
            </h1>
          </div>
          <p className="text-[14px] text-[var(--text-muted)]">
            Created {new Date(order.createdAt).toLocaleString()}
          </p>
        </div>

        {/* Status chip + force-end for a live delivery */}
        <div className="flex flex-col items-end gap-3 mt-8">
          <span
            className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${
              statusChip[order.status] ?? 'bg-[var(--bg-base)] text-[var(--text-muted)]'
            }`}
          >
            {order.status.replace(/_/g, ' ')}
          </span>
          {!DELIVERY_ENDED_STATUSES.includes(order.status) && (
            <button
              onClick={() => setShowForceEnd(true)}
              className="rounded-full border border-[var(--state-danger-border)] bg-[var(--state-danger-bg)] px-4 py-2 text-[12px] font-medium text-[var(--state-danger)] hover:border-[var(--state-danger)]"
            >
              Force end trip
            </button>
          )}
        </div>
      </div>

      <ForceEndTripModal
        open={showForceEnd}
        onClose={() => setShowForceEnd(false)}
        orderId={order.id}
        subject={`Order #${order.branchOrderNumber || order.id.slice(0, 8)}`}
        onDone={() => queryClient.invalidateQueries({ queryKey: ['order', orderId] })}
      />

      {/* Summary cards: pricing + details side by side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Pricing card */}
        <div className="rounded-3xl bg-white shadow-soft p-6 space-y-1">
          <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] mb-4">
            Pricing
          </p>

          <div className="flex justify-between border-b border-[var(--border-subtle)] py-3">
            <span className="text-[13px] text-[var(--text-muted)]">Subtotal</span>
            <span className="text-[14px] text-[var(--text-primary)]">
              {formatCurrency(order.subtotal)}
            </span>
          </div>

          <div className="flex justify-between border-b border-[var(--border-subtle)] py-3">
            <span className="text-[13px] text-[var(--text-muted)]">Delivery fee</span>
            <span className="text-[14px] text-[var(--text-primary)]">
              {formatCurrency(order.deliveryFee)}
            </span>
          </div>

          <div className="flex justify-between border-b border-[var(--border-subtle)] py-3">
            <span className="text-[13px] text-[var(--text-muted)]">Tax</span>
            <span className="text-[14px] text-[var(--text-primary)]">
              {formatCurrency(order.taxAmount)}
            </span>
          </div>

          <div className="flex justify-between border-b border-[var(--border-subtle)] py-3">
            <span className="text-[13px] text-[var(--text-muted)]">Discount</span>
            <span className="text-[14px] text-[var(--state-danger)]">
              -{formatCurrency(order.discountAmount)}
            </span>
          </div>

          <div className="flex justify-between pt-3">
            <span className="text-[14px] font-semibold text-[var(--text-primary)]">Total</span>
            <span className="text-[17px] font-semibold text-[var(--text-primary)]">
              {formatCurrency(order.totalAmount)}
            </span>
          </div>
        </div>

        {/* Details card */}
        <div className="rounded-3xl bg-white shadow-soft p-6">
          <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] mb-4">
            Details
          </p>

          {order.branchName && (
            <div className="flex justify-between border-b border-[var(--border-subtle)] py-3">
              <span className="text-[13px] text-[var(--text-muted)]">Branch</span>
              <span className="text-[14px] text-[var(--text-primary)]">{order.branchName}</span>
            </div>
          )}

          {order.deliveryType && (
            <div className="flex justify-between border-b border-[var(--border-subtle)] py-3">
              <span className="text-[13px] text-[var(--text-muted)]">Delivery type</span>
              <span className="text-[14px] text-[var(--text-primary)] capitalize">{order.deliveryType}</span>
            </div>
          )}

          {order.paymentStatus && (
            <div className="flex justify-between border-b border-[var(--border-subtle)] py-3">
              <span className="text-[13px] text-[var(--text-muted)]">Payment</span>
              <span className="text-[14px] text-[var(--text-primary)]">{order.paymentStatus.replace(/_/g, ' ')}</span>
            </div>
          )}

          <div className="flex justify-between border-b border-[var(--border-subtle)] py-3">
            <span className="text-[13px] text-[var(--text-muted)]">Delivery paid by</span>
            <span className="text-[14px] text-[var(--text-primary)] capitalize">
              {order.deliveryPaidBy ?? '—'}
            </span>
          </div>

          {order.deliveryAddress != null && (
            <div className="flex justify-between border-b border-[var(--border-subtle)] py-3 gap-4">
              <span className="text-[13px] text-[var(--text-muted)] shrink-0">Delivery address</span>
              <span className="text-[14px] text-[var(--text-primary)] text-right">
                {[
                  (order.deliveryAddress as Record<string, unknown>).streetLine1,
                  (order.deliveryAddress as Record<string, unknown>).city,
                  (order.deliveryAddress as Record<string, unknown>).county,
                ].filter(Boolean).join(', ') || '—'}
              </span>
            </div>
          )}

          {order.cancellationReason && (
            <div className="flex justify-between border-b border-[var(--border-subtle)] py-3 gap-4">
              <span className="text-[13px] text-[var(--text-muted)] shrink-0">Cancelled</span>
              <span className="text-[14px] text-[var(--state-danger)] text-right">{order.cancellationReason}</span>
            </div>
          )}

          {order.customerNotes && (
            <div className="flex justify-between border-b border-[var(--border-subtle)] py-3 gap-4">
              <span className="text-[13px] text-[var(--text-muted)] shrink-0">Customer notes</span>
              <span className="text-[14px] text-[var(--text-primary)] text-right">{order.customerNotes}</span>
            </div>
          )}

          {order.receiveCode && (
            <div className="flex justify-between border-b border-[var(--border-subtle)] py-3">
              <span className="text-[13px] text-[var(--text-muted)]">Receive code</span>
              <span className="text-[14px] font-mono text-[var(--text-primary)]">
                {order.receiveCode}
              </span>
            </div>
          )}

          {order.buyerConfirmedAt && (
            <div className="flex justify-between border-b border-[var(--border-subtle)] py-3">
              <span className="text-[13px] text-[var(--text-muted)]">Buyer confirmed</span>
              <span className="text-[14px] text-[var(--text-primary)]">
                {new Date(order.buyerConfirmedAt).toLocaleString()}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Order items */}
      {order.items && order.items.length > 0 && (
        <div className="rounded-3xl bg-white shadow-soft overflow-hidden">
          <div className="px-6 pt-6 pb-4 border-b border-[var(--border-subtle)]">
            <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
              Items
            </p>
            <p className="text-[17px] font-semibold text-[var(--text-primary)] mt-0.5">
              {order.items.length} {order.items.length === 1 ? 'item' : 'items'}
            </p>
          </div>

          <div className="divide-y divide-[var(--border-subtle)]">
            {order.items.map((item) => (
              <div
                key={item.id}
                className="flex items-center justify-between px-6 py-4 hover:bg-[var(--bg-base)] transition-colors"
              >
                <div className="space-y-0.5">
                  <p className="text-[14px] font-medium text-[var(--text-primary)]">
                    {item.productName}
                  </p>
                  <p className="text-[13px] text-[var(--text-muted)]">
                    {item.quantity} × {formatCurrency(item.unitPrice)}
                  </p>
                </div>
                <span className="text-[14px] font-semibold text-[var(--text-primary)]">
                  {formatCurrency(item.totalPrice)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Status timeline */}
      {order.statusHistory && order.statusHistory.length > 0 && (
        <div className="rounded-3xl bg-white shadow-soft overflow-hidden">
          <div className="px-6 pt-6 pb-4 border-b border-[var(--border-subtle)]">
            <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
              Timeline
            </p>
            <p className="text-[17px] font-semibold text-[var(--text-primary)] mt-0.5">
              Status history
            </p>
          </div>

          <div className="divide-y divide-[var(--border-subtle)]">
            {order.statusHistory.map((entry) => (
              <div
                key={entry.id}
                className="flex items-center gap-4 px-6 py-4 hover:bg-[var(--bg-base)] transition-colors"
              >
                {/* Timeline dot */}
                <div className="w-2 h-2 rounded-full bg-[var(--gradient-to)] flex-shrink-0" />

                {/* Transition label */}
                <div className="flex-1 min-w-0">
                  <p className="text-[14px] font-medium text-[var(--text-primary)]">
                    {entry.fromStatus || '—'} → {entry.toStatus}
                  </p>
                  {entry.notes && (
                    <p className="text-[13px] text-[var(--text-muted)] truncate">{entry.notes}</p>
                  )}
                </div>

                {/* Timestamp */}
                <div className="flex items-center gap-1.5 text-[var(--text-muted)] flex-shrink-0">
                  <Clock className="w-3.5 h-3.5" strokeWidth={1.5} />
                  <span className="text-[13px]">
                    {new Date(entry.createdAt).toLocaleString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
